import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ExecMonthlyRow,
  type ExecutiveMetricKey,
  formatMetric,
  formatMonthLabel,
  METRIC_CONFIG,
  METRIC_ORDER,
} from "./types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
);

const BRAND_NAVY = "#0E3752";
const BRAND_GOLD = "#D19D62";

interface MetricTrendChartsProps {
  currentRows: ExecMonthlyRow[];
  priorRows?: (ExecMonthlyRow | null)[];
  currentLabel?: string;
  priorLabel?: string;
}

export function MetricTrendCharts({
  currentRows,
  priorRows,
  currentLabel = "Current",
  priorLabel = "Prior year",
}: MetricTrendChartsProps) {
  const labels = currentRows.map((r) => formatMonthLabel(r.month));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {METRIC_ORDER.map((key) => (
        <MetricChart
          key={key}
          metricKey={key}
          currentRows={currentRows}
          priorRows={priorRows}
          labels={labels}
          currentLabel={currentLabel}
          priorLabel={priorLabel}
        />
      ))}
    </div>
  );
}

function MetricChart({
  metricKey,
  currentRows,
  priorRows,
  labels,
  currentLabel,
  priorLabel,
}: {
  metricKey: ExecutiveMetricKey;
  currentRows: ExecMonthlyRow[];
  priorRows?: (ExecMonthlyRow | null)[];
  labels: string[];
  currentLabel: string;
  priorLabel: string;
}) {
  const { label, format } = METRIC_CONFIG[metricKey];
  const scale = (v: number) => (format === "percent" ? v * 100 : v);
  const currentData = currentRows.map((r) => scale(r[metricKey]));
  const priorData = priorRows?.map((r) => (r ? scale(r[metricKey]) : null));
  const hasPrior = !!priorData;

  const datasets = [
    {
      label: currentLabel,
      data: currentData,
      borderColor: BRAND_NAVY,
      backgroundColor: `${BRAND_GOLD}33`,
      pointBackgroundColor: BRAND_GOLD,
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.25,
      fill: !hasPrior,
      borderWidth: 2,
    },
  ];
  if (hasPrior) {
    datasets.push({
      label: priorLabel,
      data: priorData as number[],
      borderColor: `${BRAND_GOLD}`,
      backgroundColor: "transparent",
      pointBackgroundColor: BRAND_GOLD,
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.25,
      fill: false,
      borderWidth: 2,
      // biome-ignore lint/suspicious/noExplicitAny: chart.js dataset shape varies
    } as any);
    (datasets[1] as unknown as { borderDash: number[] }).borderDash = [4, 4];
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground tracking-wide">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="h-56">
        <Line
          data={{ labels, datasets }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: hasPrior,
                position: "bottom",
                labels: { boxWidth: 12, font: { size: 10 } },
              },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const idx = items[0]?.dataIndex ?? 0;
                    const row = currentRows[idx];
                    return row
                      ? new Date(`${row.month}-01`).toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })
                      : "";
                  },
                  label: (item) => {
                    const idx = item.dataIndex;
                    const isPrior = item.datasetIndex === 1;
                    const row = isPrior ? priorRows?.[idx] : currentRows[idx];
                    const value = row ? row[metricKey] : null;
                    const text =
                      value === null || value === undefined ? "—" : formatMetric(value, format);
                    return ` ${item.dataset.label}: ${text}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: 12,
                  font: { size: 10 },
                },
              },
              y: {
                grid: { color: "rgba(0,0,0,0.05)" },
                ticks: {
                  font: { size: 10 },
                  callback: (value) => {
                    const n = Number(value);
                    if (format === "currency") {
                      if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
                      if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
                      return `$${n}`;
                    }
                    if (format === "percent") return `${n.toFixed(0)}%`;
                    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
                    return n;
                  },
                },
              },
            },
          }}
        />
      </CardContent>
    </Card>
  );
}
